import { HttpContext, HttpContextToken } from '@angular/common/http'

/**
 * Opt-in toggle for FormFeedbackInterceptor.
 * Set to true to enable success/error snackbar toast for a specific mutation request.
 * Default OFF — most POST/PATCH calls in this codebase are used to fetch data
 * (vd `api.service.ts` pagination loader, `exportFile`, sensor data fetch),
 * default-on would false-toast those.
 */
export const SHOW_FEEDBACK = new HttpContextToken<boolean>(() => false)

/**
 * Optional override for success toast message.
 * Pass an i18n key (vd 'USERS.CREATED_SUCCESSFULLY') to customize the success message.
 * If null, falls back to BE response `data.message` or generic 'COMMON.SAVED_SUCCESSFULLY'.
 */
export const SUCCESS_MESSAGE = new HttpContextToken<string | null>(() => null)

/**
 * Whether the interceptor shows an error toast on a failed request. Default true.
 * Set to false (via `withFeedback(key, { onError: false })`) for callers that already
 * surface errors themselves (vd a confirm dialog or an inline form error) so the toast
 * only confirms success and the error path is not double-reported.
 */
export const FEEDBACK_ON_ERROR = new HttpContextToken<boolean>(() => true)

/**
 * Helper: build an HttpContext with feedback opted in.
 *
 * @example
 *   this.http.post('/api/users', body, { context: withFeedback() })
 *   this.http.post('/api/users', body, { context: withFeedback('USERS.CREATED_SUCCESSFULLY') })
 *   // success-only — keep this caller's own error dialog/inline message:
 *   this.http.post('/api/users', body, { context: withFeedback(null, { onError: false }) })
 */
export function withFeedback(successMessageKey: string | null = null, options?: { onError?: boolean }): HttpContext {
  const context = new HttpContext().set(SHOW_FEEDBACK, true)
  if (successMessageKey) {
    context.set(SUCCESS_MESSAGE, successMessageKey)
  }
  if (options?.onError === false) {
    context.set(FEEDBACK_ON_ERROR, false)
  }
  return context
}
